import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../../components/Navbar';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Checkbox } from '../../components/ui/checkbox';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus } from 'lucide-react';

const ProviderAddWarehouse = () => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    address: '',
    city: '',
    state: '',
    postal_code: '',
    country: 'USA',
    latitude: '',
    longitude: '',
    storage_types: [],
    bulk_floor_sqm: '',
    racking_positions: '',
    variable_cbm: '',
    temperature_controlled: false,
    temperature_range: '',
    minimum_commitment_months: '1',
    gate_charges_inbound: '',
    gate_charges_outbound: '',
    price_per_sqm: '',
    price_per_pp: '',
    price_per_cbm: ''
  });
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

  const storageTypeOptions = [
    { value: 'bulk_floor', label: 'Bulk Floor' },
    { value: 'racking', label: 'Racking' },
    { value: 'variable', label: 'Variable (CBM)' }
  ];

  const handleStorageTypeToggle = (value) => {
    const currentTypes = formData.storage_types;
    if (currentTypes.includes(value)) {
      setFormData({ ...formData, storage_types: currentTypes.filter(t => t !== value) });
    } else {
      setFormData({ ...formData, storage_types: [...currentTypes, value] });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.storage_types.length === 0) {
      toast.error('Please select at least one storage type');
      return;
    }

    setLoading(true);
    try {
      const payload = {
        ...formData,
        latitude: parseFloat(formData.latitude),
        longitude: parseFloat(formData.longitude),
        bulk_floor_sqm: parseFloat(formData.bulk_floor_sqm) || 0,
        racking_positions: parseInt(formData.racking_positions) || 0,
        variable_cbm: parseFloat(formData.variable_cbm) || 0,
        minimum_commitment_months: parseInt(formData.minimum_commitment_months) || 1,
        gate_charges_inbound: parseFloat(formData.gate_charges_inbound) || 0,
        gate_charges_outbound: parseFloat(formData.gate_charges_outbound) || 0,
        price_per_sqm: parseFloat(formData.price_per_sqm) || 0,
        price_per_pp: parseFloat(formData.price_per_pp) || 0,
        price_per_cbm: parseFloat(formData.price_per_cbm) || 0,
        location: {
          address: formData.address,
          city: formData.city,
          state: formData.state,
          postal_code: formData.postal_code,
          country: formData.country,
          latitude: parseFloat(formData.latitude),
          longitude: parseFloat(formData.longitude)
        }
      };

      await axios.post(`${API_URL}/warehouses`, payload);
      toast.success('Warehouse added successfully');
      navigate('/provider/warehouses');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add warehouse');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-secondary">
      <Navbar />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="font-heading text-4xl font-black text-foreground tracking-tight mb-2">
            Add Warehouse
          </h1>
          <p className="text-muted-foreground">List a new warehouse space on Plinthion</p>
        </div>

        <Card className="bg-white border border-border rounded-sm shadow-sm p-6">
          <form onSubmit={handleSubmit} className="space-y-6" data-testid="add-warehouse-form">
            <div className="space-y-4">
              <h3 className="font-heading text-lg font-bold text-foreground">Basic Information</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Warehouse Name *</Label>
                  <Input
                    id="name"
                    data-testid="name-input"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    className="rounded-sm"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="city">City *</Label>
                  <Input
                    id="city"
                    data-testid="city-input"
                    value={formData.city}
                    onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                    required
                    className="rounded-sm"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  data-testid="description-input"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={3}
                  className="rounded-sm"
                />
              </div>
            </div>

            <div className="space-y-4">
              <h3 className="font-heading text-lg font-bold text-foreground">Location Details</h3>
              
              <div className="space-y-2">
                <Label htmlFor="address">Address *</Label>
                <Input
                  id="address"
                  data-testid="address-input"
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  required
                  className="rounded-sm"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="state">State *</Label>
                  <Input
                    id="state"
                    data-testid="state-input"
                    value={formData.state}
                    onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                    required
                    className="rounded-sm"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="postal_code">Postal Code *</Label>
                  <Input
                    id="postal_code"
                    data-testid="postal-code-input"
                    value={formData.postal_code}
                    onChange={(e) => setFormData({ ...formData, postal_code: e.target.value })}
                    required
                    className="rounded-sm"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="country">Country *</Label>
                  <Input
                    id="country"
                    data-testid="country-input"
                    value={formData.country}
                    onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                    required
                    className="rounded-sm"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="latitude">Latitude *</Label>
                  <Input
                    id="latitude"
                    type="number"
                    step="any"
                    data-testid="latitude-input"
                    value={formData.latitude}
                    onChange={(e) => setFormData({ ...formData, latitude: e.target.value })}
                    required
                    className="rounded-sm"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="longitude">Longitude *</Label>
                  <Input
                    id="longitude"
                    type="number"
                    step="any"
                    data-testid="longitude-input"
                    value={formData.longitude}
                    onChange={(e) => setFormData({ ...formData, longitude: e.target.value })}
                    required
                    className="rounded-sm"
                  />
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <h3 className="font-heading text-lg font-bold text-foreground">Storage Configuration</h3>
              
              <div className="space-y-3">
                <Label>Storage Types *</Label>
                {storageTypeOptions.map((option) => (
                  <div key={option.value} className="flex items-center space-x-2">
                    <Checkbox
                      id={option.value}
                      data-testid={`storage-type-${option.value}`}
                      checked={formData.storage_types.includes(option.value)}
                      onCheckedChange={() => handleStorageTypeToggle(option.value)}
                    />
                    <Label htmlFor={option.value} className="cursor-pointer">{option.label}</Label>
                  </div>
                ))}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="bulk_floor_sqm">Bulk Floor (SQM)</Label>
                  <Input
                    id="bulk_floor_sqm"
                    type="number"
                    step="0.01"
                    data-testid="bulk-floor-input"
                    value={formData.bulk_floor_sqm}
                    onChange={(e) => setFormData({ ...formData, bulk_floor_sqm: e.target.value })}
                    className="rounded-sm"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="racking_positions">Racking Positions (PP)</Label>
                  <Input
                    id="racking_positions"
                    type="number"
                    data-testid="racking-input"
                    value={formData.racking_positions}
                    onChange={(e) => setFormData({ ...formData, racking_positions: e.target.value })}
                    className="rounded-sm"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="variable_cbm">Variable Storage (CBM)</Label>
                  <Input
                    id="variable_cbm"
                    type="number"
                    step="0.01"
                    data-testid="variable-cbm-input"
                    value={formData.variable_cbm}
                    onChange={(e) => setFormData({ ...formData, variable_cbm: e.target.value })}
                    className="rounded-sm"
                  />
                </div>
              </div>

              <div className="flex items-center space-x-2">
                <Checkbox
                  id="temperature_controlled"
                  data-testid="temperature-checkbox"
                  checked={formData.temperature_controlled}
                  onCheckedChange={(checked) => setFormData({ ...formData, temperature_controlled: checked })}
                />
                <Label htmlFor="temperature_controlled" className="cursor-pointer">Temperature Controlled</Label>
              </div>

              {formData.temperature_controlled && (
                <div className="space-y-2">
                  <Label htmlFor="temperature_range">Temperature Range</Label>
                  <Input
                    id="temperature_range"
                    data-testid="temperature-range-input"
                    placeholder="e.g., -5°C to 25°C"
                    value={formData.temperature_range}
                    onChange={(e) => setFormData({ ...formData, temperature_range: e.target.value })}
                    className="rounded-sm"
                  />
                </div>
              )}
            </div>

            <div className="space-y-4">
              <h3 className="font-heading text-lg font-bold text-foreground">Pricing & Terms</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="price_per_sqm">Price per SQM ($)</Label>
                  <Input
                    id="price_per_sqm"
                    type="number"
                    step="0.01"
                    data-testid="price-sqm-input"
                    value={formData.price_per_sqm}
                    onChange={(e) => setFormData({ ...formData, price_per_sqm: e.target.value })}
                    className="rounded-sm"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="price_per_pp">Price per PP ($)</Label>
                  <Input
                    id="price_per_pp"
                    type="number"
                    step="0.01"
                    data-testid="price-pp-input"
                    value={formData.price_per_pp}
                    onChange={(e) => setFormData({ ...formData, price_per_pp: e.target.value })}
                    className="rounded-sm"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="price_per_cbm">Price per CBM ($)</Label>
                  <Input
                    id="price_per_cbm"
                    type="number"
                    step="0.01"
                    data-testid="price-cbm-input"
                    value={formData.price_per_cbm}
                    onChange={(e) => setFormData({ ...formData, price_per_cbm: e.target.value })}
                    className="rounded-sm"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="minimum_commitment_months">Min. Commitment (Months)</Label>
                  <Input
                    id="minimum_commitment_months"
                    type="number"
                    data-testid="min-commitment-input"
                    value={formData.minimum_commitment_months}
                    onChange={(e) => setFormData({ ...formData, minimum_commitment_months: e.target.value })}
                    className="rounded-sm"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="gate_charges_inbound">Gate Charges Inbound ($)</Label>
                  <Input
                    id="gate_charges_inbound"
                    type="number"
                    step="0.01"
                    data-testid="gate-inbound-input"
                    value={formData.gate_charges_inbound}
                    onChange={(e) => setFormData({ ...formData, gate_charges_inbound: e.target.value })}
                    className="rounded-sm"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="gate_charges_outbound">Gate Charges Outbound ($)</Label>
                  <Input
                    id="gate_charges_outbound"
                    type="number"
                    step="0.01"
                    data-testid="gate-outbound-input"
                    value={formData.gate_charges_outbound}
                    onChange={(e) => setFormData({ ...formData, gate_charges_outbound: e.target.value })}
                    className="rounded-sm"
                  />
                </div>
              </div>
            </div>

            <div className="flex items-center gap-4 pt-6 border-t border-border">
              <Button
                type="submit"
                data-testid="submit-warehouse"
                disabled={loading}
                className="bg-accent text-accent-foreground hover:bg-accent/90 rounded-sm transition-all active:scale-95"
              >
                {loading ? 'Adding...' : (
                  <>
                    <Plus className="mr-2 h-4 w-4" strokeWidth={1.5} />
                    Add Warehouse
                  </>
                )}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate('/provider/warehouses')}
                className="rounded-sm"
              >
                Cancel
              </Button>
            </div>
          </form>
        </Card>
      </div>
    </div>
  );
};

export default ProviderAddWarehouse;